import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, Tag, ArrowLeft, ArrowRight } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useCookieConsent } from '../hooks/useCookieConsent';
import { trackEvent } from '../lib/analytics';
import SEO from '../components/SEO';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  tags: string[];
  image: string;
  language: string;
  author: string;
  featured: boolean;
  translationKey: string;
  slug: string;
  content: React.ReactNode;
}

export const Blog = () => {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const currentLanguage = i18n.language;
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAllowed } = useCookieConsent();

  useEffect(() => {
    const launchLayerPost: BlogPost = {
      id: 'launch-operational-layer',
      title: currentLanguage === 'it'
        ? "Launch: il framework operativo che trasforma le PMI"
        : 'Launch: the Operational Layer that Transforms SMEs',
      excerpt: currentLanguage === 'it'
        ? "Zero dispersione operativa, lead sempre tracciati, follow-up automatizzati. Il layer Launch combina diagnosi, architettura e gestione continuativa per dare alla PMI un sistema che lavora da solo."
        : 'No more operational drag: Launch unifies diagnostics, architecture and ongoing run so your SME captures every lead, automates follow-ups and operates on measurable KPIs.',
      date: '2025-11-06',
      readTime: currentLanguage === 'it' ? '8 min di lettura' : '8 min read',
      tags: [
        currentLanguage === 'it' ? 'Launch Layer' : 'Launch Layer',
        currentLanguage === 'it' ? 'PMI' : 'SMEs',
        currentLanguage === 'it' ? 'Operazioni' : 'Operations',
      ],
      image: '/images/Launch .webp',
      language: currentLanguage,
      author: 'Victor Lazari',
      featured: true,
      translationKey: 'launch-operational-layer',
      slug: 'launch-operational-layer',
      content: currentLanguage === 'it' ? (
        <>
          <p className="lead text-xl text-slate-300 mb-8">
            <strong>Launch</strong> è il layer operativo pensato per le PMI che vivono ancora di strumenti scollegati, file sparsi e follow-up manuali. Non è un progetto una tantum: è il sistema che combina automazioni, CRM e assistenza continua per ridurre il caos operativo e trasformarlo in vantaggio competitivo.
          </p>
          <h2>Dove si blocca davvero una PMI</h2>
          <ul className="space-y-4 my-6">
            <li className="flex items-start gap-3">
              <span className="text-cyan-500 mt-1">▸</span>
              <span><strong>Lead e richieste frammentati:</strong> arrivano da sito, WhatsApp, campagne, ma si perdono perché non esiste un unico funnel centralizzato.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-cyan-500 mt-1">▸</span>
              <span><strong>Tempo del titolare:</strong> 6–10 ore al mese spese tra fornitori, accessi dimenticati e check manuali sul team.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-cyan-500 mt-1">▸</span>
              <span><strong>Follow-up lenti:</strong> nessuna automazione ricorda appuntamenti, preventivi o recensioni. Ogni ritardo costa conversioni.</span>
            </li>
          </ul>
          <h3>La Soluzione Launch</h3>
          <p>
            Launch non è solo software. È un'architettura. Installiamo un "sistema operativo" che intercetta ogni segnale (lead, email, messaggio), lo processa e lo notifica alla persona giusta. 
            Il risultato? Tu guardi la dashboard, il sistema fa il lavoro sporco.
          </p>
          <div className="mt-10 p-8 rounded-2xl border border-cyan-500/30 bg-cyan-900/10 text-center relative overflow-hidden group">
            <div className="absolute inset-0 bg-cyan-500/5 group-hover:bg-cyan-500/10 transition-colors" />
            <p className="text-xs text-cyan-400 mb-4 font-mono tracking-widest uppercase">System Ready For Deployment</p>
            <a
              href="/solutions#operational-layers"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-cyan-600 text-white font-bold hover:bg-cyan-500 transition-all shadow-lg shadow-cyan-500/20"
            >
              Attiva Protocollo Launch
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </>
      ) : (
        <>
          <p className="lead text-xl text-slate-300 mb-8">
            <strong>Launch</strong> is the operational layer designed for SMEs that still survive on disconnected tools, scattered files, and manual follow-ups. It’s not a one-off project: it’s the system that combines automations, CRM, and continuous assistance to reduce operational chaos and turn it into a competitive advantage.
          </p>
          <h2>Where an SME Really Gets Stuck</h2>
          <ul className="space-y-4 my-6">
            <li className="flex items-start gap-3">
              <span className="text-cyan-500 mt-1">▸</span>
              <span><strong>Fragmented Leads:</strong> They arrive from the site, WhatsApp, campaigns, but get lost because there is no single centralized funnel.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-cyan-500 mt-1">▸</span>
              <span><strong>Owner's Time:</strong> 6–10 hours a month spent between suppliers, forgotten logins, and manual team checks.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-cyan-500 mt-1">▸</span>
              <span><strong>Slow Follow-ups:</strong> No automation remembers appointments, quotes, or reviews. Every delay costs conversions.</span>
            </li>
          </ul>
          <h3>The Launch Solution</h3>
          <p>
            Launch is not just software. It is an architecture. We install an "operating system" that intercepts every signal (lead, email, message), processes it, and notifies the right person.
            The result? You watch the dashboard, the system does the grunt work.
          </p>
          <div className="mt-10 p-8 rounded-2xl border border-cyan-500/30 bg-cyan-900/10 text-center relative overflow-hidden group">
            <div className="absolute inset-0 bg-cyan-500/5 group-hover:bg-cyan-500/10 transition-colors" />
            <p className="text-xs text-cyan-400 mb-4 font-mono tracking-widest uppercase">System Ready For Deployment</p>
            <a
              href="/solutions#operational-layers"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-cyan-600 text-white font-bold hover:bg-cyan-500 transition-all shadow-lg shadow-cyan-500/20"
            >
              Activate Launch Protocol
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </>
      )
    };

    const aiWosPost: BlogPost = {
      id: 'ai-wos',
      title: currentLanguage === 'it'
        ? "Vector + Cortex: Il Sistema Nervoso Digitale per Aziende che Vogliono Scalare"
        : "Vector + Cortex: The Digital Nervous System Built to Scale Your Company",
      excerpt: currentLanguage === 'it'
        ? "Cortex ascolta tutto, Vector fa succedere le cose. Insieme trasformano caos operativo in decisioni automatiche."
        : "Cortex hears every signal, Vector executes the play. Together they turn operational chaos into decisive action.",
      date: '2025-03-23',
      readTime: currentLanguage === 'it' ? '7 min di lettura' : '7 min read',
      tags: ['AI', currentLanguage === 'it' ? 'Processi' : 'Operations', currentLanguage === 'it' ? 'Automazione' : 'Automation'],
      image: '/images/vector product logo.webp',
      language: currentLanguage,
      author: 'Victor Lazari',
      featured: true,
      translationKey: 'ai-wos',
      slug: 'ai-wos',
      content: currentLanguage === 'it' ? (
        <>
          <blockquote className="border-l-4 border-purple-500 pl-6 italic text-slate-400 bg-purple-900/10 p-4 rounded-r-xl">
            <p>"Le aziende che non riescono a 'sapere' e 'agire' nello stesso istante diventano lente. E nel mercato moderno, lento significa morto."</p>
          </blockquote>
          <h2>Il nuovo sistema nervoso dell'impresa</h2>
          <p>
            <strong>Vector + Cortex</strong> rappresentano l'evoluzione del software aziendale. Non più strumenti passivi dove inserisci dati (data-entry manuale), ma agenti attivi che lavorano per te.
          </p>
          <h3>Il problema dei Silos</h3>
          <p>
            Oggi le aziende hanno dati ovunque: gestionale (ERP), email, file Excel sparsi, chat di WhatsApp. Nessuno di questi sistemi parla con l'altro.
            Il venditore non sa se l'amministrazione ha fatturato. Il marketing non sa se il lead è stato chiamato.
          </p>
          <h3>La Soluzione Integrata</h3>
          <ul className="space-y-6 my-8">
            <li className="bg-slate-900/50 p-6 rounded-xl border border-slate-800">
              <strong className="text-cyan-400 block mb-2 text-lg">1. Cortex (Il Cervello)</strong>
              Analizza le conversazioni, capisce l'intento del cliente, estrae dati non strutturati da email, PDF e documenti. Non si limita a "salvare", lui <em>capisce</em>.
            </li>
            <li className="bg-slate-900/50 p-6 rounded-xl border border-slate-800">
              <strong className="text-purple-400 block mb-2 text-lg">2. Vector (Il Sistema Nervoso)</strong>
              Prende quei dati e li muove. Crea il preventivo, aggiorna il CRM, notifica il venditore su Slack/Teams, pianifica la spedizione. Esegue l'azione fisica digitale.
            </li>
          </ul>
          <h3>Il Risultato</h3>
          <p>
            Un'azienda che reagisce in tempo reale, 24/7, con precisione chirurgica. Meno errori umani, zero tempi morti, scalabilità infinita.
          </p>
        </>
      ) : (
        <>
          <blockquote className="border-l-4 border-purple-500 pl-6 italic text-slate-400 bg-purple-900/10 p-4 rounded-r-xl">
            <p>"Companies that fail to 'know' and 'act' in the same instant become slow. And in the modern market, slow means dead."</p>
          </blockquote>
          <h2>The New Corporate Nervous System</h2>
          <p>
            <strong>Vector + Cortex</strong> represent the evolution of business software. No longer passive tools where you input data (manual data-entry), but active agents working for you.
          </p>
          <h3>The Silo Problem</h3>
          <p>
            Today companies have data everywhere: ERP, email, scattered Excel files, WhatsApp chats. None of these systems talk to each other.
            Sales doesn't know if accounting has invoiced. Marketing doesn't know if the lead has been called.
          </p>
          <h3>The Integrated Solution</h3>
          <ul className="space-y-6 my-8">
            <li className="bg-slate-900/50 p-6 rounded-xl border border-slate-800">
              <strong className="text-cyan-400 block mb-2 text-lg">1. Cortex (The Brain)</strong>
              Analyzes conversations, understands client intent, extracts unstructured data from emails, PDFs, and documents. It doesn't just "save", it <em>understands</em>.
            </li>
            <li className="bg-slate-900/50 p-6 rounded-xl border border-slate-800">
              <strong className="text-purple-400 block mb-2 text-lg">2. Vector (The Nervous System)</strong>
              Takes that data and moves it. Creates the quote, updates the CRM, notifies the salesperson on Slack/Teams, schedules the shipment. Executes the digital physical action.
            </li>
          </ul>
          <h3>The Result</h3>
          <p>
            A company that reacts in real-time, 24/7, with surgical precision. Fewer human errors, zero downtime, infinite scalability.
          </p>
        </>
      )
    };
    
    const videoEmailsPost: BlogPost = {
      id: 'personalized-video-emails',
      title: currentLanguage === 'it' 
        ? "Mail con Video Personalizzati: Il Futuro della Comunicazione Aziendale e Personale" 
        : "Personalized Video Emails: The Future of Business and Personal Communication",
      excerpt: currentLanguage === 'it' 
        ? "Nel mondo iperconnesso di oggi, le comunicazioni aziendali stanno vivendo una rivoluzione. L'email tradizionale non è più sufficiente." 
        : "In today's hyperconnected world, business communications are experiencing a revolution. Traditional email is no longer enough.",
      date: '2025-02-22',
      readTime: currentLanguage === 'it' ? '5 min di lettura' : '5 min read',
      tags: ['marketing', currentLanguage === 'it' ? 'automazione' : 'automation', 'AI'],
      image: 'https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/main/video%20mail%20avatar.png',
      language: currentLanguage,
      author: 'Victor Lazari',
      featured: false,
      translationKey: 'video-emails',
      slug: 'personalized-video-emails',
      content: currentLanguage === 'it' ? (
        <>
          <p className="lead text-xl text-slate-300">
            L'email marketing tradizionale sta morendo. Tassi di apertura in calo, attenzione ai minimi storici. Il testo scritto non basta più per bucare il rumore di fondo.
          </p>
          <h2>La Rivoluzione Video</h2>
          <p>
            Immagina che ogni tuo lead riceva un video <em>personale</em> dove lo chiami per nome, citi la sua azienda e parli del suo problema specifico.
            <br/>
            <em>"Impossibile farlo a mano per 1000 lead,"</em> diresti.
          </p>
          <h3>Con l'AI è possibile.</h3>
          <p>
            Utilizziamo avatar sintetici iper-realistici (come quello che vedi nella nostra home page) per generare migliaia di video unici, in pochi minuti.
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 my-8">
            <li className="bg-slate-800 p-4 rounded-lg text-center">
              <span className="text-3xl mb-2 block">😲</span>
              <strong className="text-white block">Impatto Emotivo</strong>
              <span className="text-sm text-slate-400">Il volto umano cattura l'attenzione all'istante.</span>
            </li>
            <li className="bg-slate-800 p-4 rounded-lg text-center">
              <span className="text-3xl mb-2 block">🚀</span>
              <strong className="text-white block">Scalabilità</strong>
              <span className="text-sm text-slate-400">Registri una volta, l'AI genera per mille destinatari.</span>
            </li>
            <li className="bg-slate-800 p-4 rounded-lg text-center">
              <span className="text-3xl mb-2 block">📈</span>
              <strong className="text-white block">Conversione</strong>
              <span className="text-sm text-slate-400">I tassi di risposta triplicano rispetto al testo puro.</span>
            </li>
          </ul>
          <p>
            Non è fantascienza. È parte del nostro stack <strong>Launch</strong>. Rendiamo le tue comunicazioni umane, su scala industriale.
          </p>
        </>
      ) : (
        <>
          <p className="lead text-xl text-slate-300">
            Traditional email marketing is dying. Open rates are plummeting, attention spans are at historic lows. Written text is no longer enough to cut through the noise.
          </p>
          <h2>The Video Revolution</h2>
          <p>
            Imagine every single one of your leads receiving a <em>personal</em> video where you call them by name, mention their company, and discuss their specific problem.
            <br/>
            <em>"Impossible to do manually for 1000 leads,"</em> you'd say.
          </p>
          <h3>With AI, it is possible.</h3>
          <p>
            We use hyper-realistic synthetic avatars (like the one you see on our home page) to generate thousands of unique videos in minutes.
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 my-8">
            <li className="bg-slate-800 p-4 rounded-lg text-center">
              <span className="text-3xl mb-2 block">😲</span>
              <strong className="text-white block">Emotional Impact</strong>
              <span className="text-sm text-slate-400">The human face captures attention instantly.</span>
            </li>
            <li className="bg-slate-800 p-4 rounded-lg text-center">
              <span className="text-3xl mb-2 block">🚀</span>
              <strong className="text-white block">Scalability</strong>
              <span className="text-sm text-slate-400">Record once, AI generates for a thousand recipients.</span>
            </li>
            <li className="bg-slate-800 p-4 rounded-lg text-center">
              <span className="text-3xl mb-2 block">📈</span>
              <strong className="text-white block">Conversion</strong>
              <span className="text-sm text-slate-400">Response rates triple compared to plain text.</span>
            </li>
          </ul>
          <p>
            It's not sci-fi. It's part of our <strong>Launch</strong> stack. We make your communications human, at an industrial scale.
          </p>
        </>
      )
    };

    const pmiDigitalPost: BlogPost = {
      id: 'why-pmi-fall-behind',
      title: currentLanguage === 'it'
        ? "Perché tante PMI italiane restano indietro? I 3 errori che vedo ogni giorno"
        : "Why Do So Many Italian SMEs Fall Behind? The 3 Mistakes I See Every Day",
      excerpt: currentLanguage === 'it'
        ? "Non è colpa della crisi. Né dell'AI. È che molte aziende continuano a commettere sempre gli stessi errori."
        : "It's not the crisis's fault. Nor is it AI's. It's that many companies keep making the same mistakes.",
      date: '2025-03-27',
      readTime: currentLanguage === 'it' ? '6 min di lettura' : '6 min read',
      tags: ['Business', currentLanguage === 'it' ? 'Trasformazione Digitale' : 'Digital Transformation', currentLanguage === 'it' ? 'PMI' : 'SMEs'],
      image: 'https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/main/process%20mapping.png',
      language: currentLanguage,
      author: 'Victor Lazari',
      featured: true,
      translationKey: 'why-pmi-fall-behind',
      slug: 'why-pmi-fall-behind',
      content: currentLanguage === 'it' ? (
        <>
          <p className="lead text-xl text-slate-300">
            Vedo centinaia di PMI lottare ogni anno. Spoiler: quasi mai mancano i soldi. Quello che manca è il <strong>metodo</strong>.
          </p>
          <h2>I 3 Errori Fatali</h2>
          <div className="space-y-8 my-8">
            <div>
              <h3 className="text-red-400 flex items-center gap-2">
                <span className="text-2xl">1.</span> Software ≠ Soluzione
              </h3>
              <p>
                Comprare HubSpot, Salesforce o l'ultimo tool AI non risolve il tuo processo di vendita se il processo non esiste su carta. È come comprare una Ferrari senza avere la patente. Prima l'architettura, poi il tool.
              </p>
            </div>
            <div>
              <h3 className="text-red-400 flex items-center gap-2">
                <span className="text-2xl">2.</span> Delega "Cieca"
              </h3>
              <p>
                Affidare il "digitale" al cugino smanettone o all'agenzia esterna senza che l'imprenditore capisca le metriche. 
                <em>"Mi fanno le campagne su Facebook."</em> Ok, ma qual è il CAC? Qual è il LTV? Se non lo controlli, non è un tuo asset.
              </p>
            </div>
            <div>
              <h3 className="text-red-400 flex items-center gap-2">
                <span className="text-2xl">3.</span> Ignorare i Dati
              </h3>
              <p>
                Prendere decisioni strategiche basate su <em>"abbiamo sempre fatto così"</em> o <em>"secondo me..."</em> invece che guardare dashboard in tempo reale.
              </p>
            </div>
          </div>
          <div className="bg-emerald-900/20 border border-emerald-500/30 p-6 rounded-xl">
            <h3 className="text-emerald-400 mt-0">La via d'uscita</h3>
            <p className="mb-0">
              Smettere di cercare "tool" magici e iniziare a costruire "asset" proprietari. Un database pulito vale più di mille like. Un'automazione che funziona vale più di tre venditori stanchi.
            </p>
          </div>
        </>
      ) : (
        <>
          <p className="lead text-xl text-slate-300">
            I see hundreds of SMEs struggling every year. Spoiler: it's almost never a lack of money. It's a lack of <strong>method</strong>.
          </p>
          <h2>The 3 Fatal Mistakes</h2>
          <div className="space-y-8 my-8">
            <div>
              <h3 className="text-red-400 flex items-center gap-2">
                <span className="text-2xl">1.</span> Software ≠ Solution
              </h3>
              <p>
                Buying HubSpot, Salesforce, or the latest AI tool doesn't fix your sales process if the process doesn't exist on paper. It's like buying a Ferrari without a license. Architecture first, then tools.
              </p>
            </div>
            <div>
              <h3 className="text-red-400 flex items-center gap-2">
                <span className="text-2xl">2.</span> "Blind" Delegation
              </h3>
              <p>
                Handing "digital" to a tech-savvy cousin or an external agency without the owner understanding the metrics.
                <em>"They run Facebook ads for me."</em> Okay, but what is the CAC? What is the LTV? If you don't control it, it's not your asset.
              </p>
            </div>
            <div>
              <h3 className="text-red-400 flex items-center gap-2">
                <span className="text-2xl">3.</span> Ignoring Data
              </h3>
              <p>
                Making strategic decisions based on <em>"we've always done it this way"</em> or <em>"I feel like..."</em> instead of looking at real-time dashboards.
              </p>
            </div>
          </div>
          <div className="bg-emerald-900/20 border border-emerald-500/30 p-6 rounded-xl">
            <h3 className="text-emerald-400 mt-0">The Way Out</h3>
            <p className="mb-0">
              Stop looking for magic "tools" and start building proprietary "assets". A clean database is worth more than a thousand likes. An automation that works is worth more than three tired salespeople.
            </p>
          </div>
        </>
      )
    };

    // Set the posts and filter by language
    const allPosts = [launchLayerPost, aiWosPost, videoEmailsPost, pmiDigitalPost].filter(post => post.language === currentLanguage);
    setPosts(allPosts);
  }, [currentLanguage]);

  // Find current post if slug is provided
  const currentPost = slug ? posts.find(post => post.slug === slug) : null;

  // If a slug is provided but no matching post was found
  if (slug && !currentPost && posts.length > 0) {
    navigate('/blog');
    return null;
  }

  // Example of using isAllowed to conditionally track an event
  const handleReadBlogPost = (postSlug: string, postTitle: string) => {
    if (isAllowed('analytics')) {
      // Only track if analytics cookies are allowed
      trackEvent('Blog', 'Read Post', `${postSlug}: ${postTitle}`);
    }
    // Continue with navigation logic
  };

  // Render single blog post
  if (currentPost) {
    return (
      <div className="relative pt-24 pb-12 px-4 min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-cyan-500/30">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px] pointer-events-none" />
        
        <SEO
          title={`${currentPost.title} – Victor Lazari`}
          description={currentPost.excerpt}
          canonical={`https://victorlazari.com/blog/${currentPost.slug}`}
          image={currentPost.image}
          type="article"
          publishedTime={new Date(currentPost.date).toISOString()}
          jsonLd={{
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: currentPost.title,
            datePublished: currentPost.date,
            author: {
              '@type': 'Person',
              name: 'Victor Lazari'
            },
            image: [currentPost.image],
            mainEntityOfPage: `https://victorlazari.com/blog/${currentPost.slug}`
          }}
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="max-w-4xl mx-auto mb-8 relative z-10"
        >
          <Link 
            to="/blog" 
            className="inline-flex items-center text-cyan-400 hover:text-cyan-300 transition-colors font-mono text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('blog.backToList') || 'RETURN_TO_INDEX'}
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto bg-slate-900/50 rounded-3xl overflow-hidden border border-slate-800 shadow-2xl backdrop-blur-xl mb-12 relative z-10"
        >
          <div className="relative h-64 md:h-96 overflow-hidden">
            <img
              src={currentPost.image}
              alt={currentPost.title}
              className="w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent" />
            <div className="absolute top-6 left-6 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] rounded bg-black/50 text-cyan-400 border border-cyan-500/30 backdrop-blur-md">
                  {currentPost.slug.toUpperCase().substring(0, 8)}
            </div>
          </div>

          <div className="p-8 md:p-12">
            <div className="flex flex-wrap gap-2 mb-8">
              {currentPost.tags.map(tag => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs font-mono rounded bg-slate-800 text-slate-300 border border-slate-700 flex items-center gap-2"
                >
                  <Tag className="w-3 h-3 text-cyan-500" />
                  {tag.toUpperCase()}
                </span>
              ))}
            </div>

            <h1 className="text-3xl md:text-5xl font-bold mb-8 text-white leading-tight">
              {currentPost.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-sm text-slate-400 mb-12 font-mono border-b border-slate-800 pb-8">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-cyan-500" />
                {new Date(currentPost.date).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-cyan-500" />
                {currentPost.readTime}
              </span>
              <span className="flex items-center gap-2 text-emerald-500">
                <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                {isAllowed('analytics') ? 'TRACKING_ACTIVE' : 'TRACKING_DISABLED'}
              </span>
            </div>

            <div className="prose prose-lg prose-invert max-w-none prose-headings:font-bold prose-headings:text-white prose-a:text-cyan-400 hover:prose-a:text-cyan-300 prose-strong:text-white prose-code:text-pink-400 prose-blockquote:border-l-cyan-500 prose-blockquote:bg-slate-800/30 prose-blockquote:p-4 prose-blockquote:rounded-r-lg">
              {currentPost.content}
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Render blog list
  return (
    <div className="relative pt-32 pb-16 px-4 min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-cyan-500/30">
      {/* Cyber Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.05)_1px,transparent_1px)] bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" />

      <SEO
        title="Blog – Victor Lazari"
        description="Intelligence Logs & Operational Insights."
        canonical="https://victorlazari.com/blog"
        type="website"
      />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight">
            Intelligence <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">Logs</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Analisi tattiche, strategie operative e note di rilascio dal fronte della trasformazione digitale.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {posts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group relative bg-slate-900 rounded-3xl overflow-hidden border border-slate-800 hover:border-cyan-500/50 transition-all duration-500 hover:shadow-[0_0_30px_rgba(6,182,212,0.1)]"
            >
              <Link
                to={`/blog/${post.slug}`}
                onClick={() => handleReadBlogPost(post.slug, post.title)}
                className="absolute inset-0 z-20"
              >
                <span className="sr-only">View article</span>
              </Link>
              
              <div className="relative h-64 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 opacity-80 group-hover:opacity-100"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent" />
                
                <div className="absolute top-4 left-4 flex gap-2">
                   <div className="px-2 py-1 rounded bg-slate-950/80 text-[10px] font-mono font-bold text-cyan-400 border border-cyan-500/30 backdrop-blur-md">
                         {post.featured ? 'FEATURED' : 'ARCHIVE'}
                   </div>
                </div>
              </div>

              <div className="p-8">
                <div className="flex flex-wrap gap-2 mb-6">
                  {post.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-2 py-1 text-[10px] font-mono rounded bg-slate-800 text-slate-400 border border-slate-700 group-hover:border-cyan-500/30 transition-colors"
                    >
                      #{tag.toUpperCase().replace(/\s+/g, '_')}
                    </span>
                  ))}
                </div>

                <h2 className="text-2xl font-bold mb-4 text-white group-hover:text-cyan-400 transition-colors leading-tight">
                  {post.title}
                </h2>

                <p className="text-slate-400 mb-6 line-clamp-3 leading-relaxed text-sm">
                  {post.excerpt}
                </p>

                <div className="flex items-center justify-between border-t border-slate-800 pt-6">
                  <div className="flex items-center gap-4 text-xs font-mono text-slate-500">
                    <span className="flex items-center gap-2">
                      <Calendar className="w-3 h-3" />
                      {new Date(post.date).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>
                  <div className="text-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-[-10px] group-hover:translate-x-0 duration-300">
                    <ArrowRight size={16} />
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  );
};
